import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { RESET_PASSWORD_PATH } from '../../lib/recovery-redirect';
import { supabase } from '../../lib/supabase';
import { STUDENT_EMAIL_DOMAIN } from '../../shared/auth/student-login';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthLayout } from './AuthLayout';
import s from './ForgotPasswordPage.module.css';
import { forgotPasswordSchema, type ForgotPasswordInput } from './schemas';

/** Seconds before the "send it again" button re-arms. */
const RESEND_COOLDOWN_SECONDS = 45;

/**
 * Institute-provisioned students sign in with a username, and Phase 90 backs
 * that with a synthetic address on an undeliverable domain. A reset mail to one
 * of those would never arrive, so say so instead of showing the same cheerful
 * "check your inbox" as everyone else.
 */
function isUndeliverableStudentEmail(email: string): boolean {
  return email.endsWith(STUDENT_EMAIL_DOMAIN);
}

export function ForgotPasswordPage(): JSX.Element {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const sentHeading = useRef<HTMLHeadingElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [cooldown]);

  // The whole screen is replaced without a route change, so nothing would move
  // a screen reader off the form it just submitted. Put it on the new heading.
  useEffect(() => {
    if (sentTo !== null) sentHeading.current?.focus();
  }, [sentTo]);

  const sendLink = useCallback(async (email: string): Promise<boolean> => {
    setSubmitError(null);
    if (isUndeliverableStudentEmail(email)) {
      setSubmitError(
        'That is an institute student login, which has no inbox attached. Ask your institute to reset your password for you.',
      );
      return false;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${RESET_PASSWORD_PATH}`,
    });
    if (error !== null) {
      // Supabase deliberately succeeds for addresses it does not know, so an
      // error here is a real one — almost always the per-address send throttle,
      // which the message states in seconds. Swallowing it is what makes this
      // feel like "I clicked it and nothing happened".
      setSubmitError(error.message);
      return false;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    return true;
  }, []);

  async function onSubmit(values: ForgotPasswordInput): Promise<void> {
    const sent = await sendLink(values.email);
    if (sent) setSentTo(values.email);
  }

  async function onResend(): Promise<void> {
    if (sentTo === null || cooldown > 0 || resending) return;
    setResending(true);
    try {
      await sendLink(sentTo);
    } finally {
      setResending(false);
    }
  }

  const pitch = (
    <>
      Locked out? <em>Not for long.</em>
    </>
  );

  if (sentTo !== null) {
    return (
      <AuthLayout
        pitchTitle={pitch}
        pitchBody="We send a single-use link that lets you set a new password. It expires quickly, so use the newest email you receive."
      >
        <span className={s.eyebrow}>Check your email</span>
        <h1 className={s.title} tabIndex={-1} ref={sentHeading}>
          Reset link sent
        </h1>
        <p className={s.subtitle}>
          If an account exists for <strong className={s.address}>{sentTo}</strong>, a password reset
          link is on its way. It expires in about an hour and can only be used once.
        </p>

        <ol className={s.steps}>
          <li>Open the email titled “Reset your password”.</li>
          <li>Check spam or promotions if it is not in your inbox within a minute.</li>
          <li>Follow the link on this device or any other — it works from either.</li>
        </ol>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        <div className={s.actions}>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            loading={resending}
            disabled={cooldown > 0}
            onClick={() => {
              void onResend();
            }}
          >
            {cooldown > 0 ? `Send it again in ${cooldown}s` : 'Send it again'}
          </Button>
          <p className={s.footer}>
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      pitchTitle={pitch}
      pitchBody="Tell us the email on your account and we'll send a link that lets you set a new password."
    >
      <span className={s.eyebrow}>Password help</span>
      <h1 className={s.title}>Forgot your password?</h1>
      <p className={s.subtitle}>
        Enter your email and we'll send a reset link. Remembered it?{' '}
        <Link to="/login">Back to login</Link>.
      </p>

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            placeholder="you@institute.edu"
            invalid={errors.email !== undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email')}
          />
          <FormError id="email-error" message={errors.email?.message} />
        </div>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Send reset link
        </Button>

        <p className={s.studentHint}>
          Signed in with a username from your institute? Your teacher resets that password for you.
        </p>
      </form>
    </AuthLayout>
  );
}
